import { queryOptions, useQuery } from '@tanstack/react-query'
import { type ProductQueryProps, type Product } from '@/features/shop/services/Product/types'
import {
  type StoreParamsAsKey,
  useStoreParams,
  type StoreParams,
} from '@/features/shared/utils/dataFetching/storeParams'
import {
  fetchPdpProduct,
  fetchProductUniversalId,
  fetchProducts,
  fetchProductsChildren,
} from '@/features/shop/services/Product/fetchers'
import { getSortedProductIds } from '@/features/shop/services/Product/utils'
import { useUserId } from '@/features/account/services/User/hooks'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'
import { getPropertiesFromColorAndSize } from '@/features/shop/services/Product/utils/gql-mapping'

type QueryOptionsParams = { storeParams: StoreParams }

export type ProductsQueryKey = readonly [string, QueryOptionsParams & { product_ids: number[] }]
export const productsQueryOptions = ({
  products,
  ...params
}: QueryOptionsParams & { products: Pick<Product, 'id'>[] }) =>
  queryOptions({
    queryKey: [
      'Products query',
      { ...params, product_ids: getSortedProductIds(products) },
    ] as const,
    queryFn: fetchProducts,
  })

export const useQueryProducts = (products: Product[], { enabled = true }: ProductQueryProps) => {
  const storeParams = useStoreParams()
  const user_id = useUserId()

  return useQuery({
    ...productsQueryOptions({ products, storeParams }),
    enabled: Boolean(user_id && enabled && products.length),
  })
}

type ProductChildrenParams = QueryOptionsParams & { parentId: number }
export type ProductChildrenQueryKey = readonly [string, ProductChildrenParams]

const productChildrenQueryOptions = (params: ProductChildrenParams) =>
  queryOptions({
    queryKey: ['Product children query', params] as const,
    queryFn: fetchProductsChildren,
  })

export const useQueryProductChildren = (parentId: number) => {
  const storeParams = useStoreParams()
  const { active: isEnhancedVariations } = useExperiment(
    ExperimentType.VARIATIONS_ENHANCED_PROPERTIES,
  )

  return useQuery({
    ...productChildrenQueryOptions({ storeParams, parentId }),
    select: (data) => {
      if (isEnhancedVariations) return data
      return data.map((product) => ({
        ...product,
        variationProperties: getPropertiesFromColorAndSize(product),
      }))
    },
    enabled: Boolean(parentId),
  })
}

type PdpProductParams = StoreParamsAsKey & { productId: number }
export type PdpProductQueryKey = readonly [string, PdpProductParams]
export const PdpProductQuery = 'PDP product'

export const pdpProductQueryOptions = ({ storeParams, productId }: PdpProductParams) =>
  queryOptions({
    queryKey: [PdpProductQuery, { storeParams, productId }] as const,
    queryFn: fetchPdpProduct,
  })

type ProductUniversalIdParams = StoreParamsAsKey & { productId: number }
export type ProductUniversalIdQueryKey = readonly [string, ProductUniversalIdParams]
export const ProductUniversalIdQuery = 'Product universal id'

export const productUniversalIdQueryOptions = ({
  storeParams,
  productId,
}: ProductUniversalIdParams) =>
  queryOptions({
    queryKey: [ProductUniversalIdQuery, { storeParams, productId }],
    queryFn: fetchProductUniversalId,
  })

export const useQueryPdpProduct = (productId: number) => {
  const storeParams = useStoreParams()
  const { store_location_id } = storeParams
  const { active: isEnhancedVariations } = useExperiment(
    ExperimentType.VARIATIONS_ENHANCED_PROPERTIES,
  )

  return useQuery({
    ...pdpProductQueryOptions({ storeParams, productId }),
    select: (data) => {
      if (!data.product || isEnhancedVariations) return data
      const mapVariationProperties = (product: Product): Product => ({
        ...product,
        variationProperties: getPropertiesFromColorAndSize(product),
      })
      return {
        ...data,
        product: {
          ...mapVariationProperties(data.product),
          variationProducts: data.product.variationProducts?.map(mapVariationProperties),
        },
      }
    },
    retryOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    enabled: Boolean(productId && store_location_id),
  })
}
